import api from '@/http/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useNavigate, useParams } from 'react-router-dom';

import OrderDetails from '@univdiam/ui/customComponents/OrderDetail/OrderDetails';
import type { OrderType } from '@/types';
import SpecialInstructions from '@univdiam/ui/customComponents/OrderDetail/SpecialInstructions';
import Attachments from '@univdiam/ui/customComponents/OrderDetail/Attachments';

import CadDesign from '@univdiam/ui/customComponents/OrderDetail/CadDesign';
import TimelineLogSection from '@univdiam/ui/customComponents/OrderDetail/TimelineLogSection';
import ErrorComponent from '@univdiam/ui/customComponents/ErrorComponent';
import axios from 'axios';
import AdminHeader from '@univdiam/ui/customComponents/OrderDetail/AdminHeader';
import { useOrderStatus, useUpdateOrder } from '@/sharedData/Order';
import { useOrderTags } from '@/sharedData/OrderTags';
import OrderAdditionalInstruction from './OrderAdditionalInstruction';
import OrderChecklist from './OrderChecklist';
import { useSocketStore } from '@/socketStore';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Chat from './Chat';
import Discussion from './Discussion';
import SectionHeader from '@univdiam/ui/customComponents/OrderDetail/SectionHeader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@univdiam/ui/components/table';
import { format } from 'date-fns';
import Loader from '@/components/Loader';
import { useAuthStore, useOrderNav } from '@/store';
import { useReactToPrint } from 'react-to-print';
import PrintTemplate from './PrintTemplate';
import { useOrderInst } from '@/sharedData/Order';
import { PackageCheck } from 'lucide-react';

const getOrder = (id: string, customer_id: string) => {
  return api.get(`/order/${id}?customer_id=${customer_id}`);
};

const getOrderLog = (order_id: string) => {
  return api.get(`/log/order/${order_id}`);
};
const getOrderCad = (order_id: string) => {
  return api.get(`/cad/${order_id}`);
};

const getOrderCadSubmiited = (order_id: string) => {
  return api.get(`/cad_order_req/${order_id}`);
};

type CADDesignType = {
  bom: JSON;
  attachments: JSON;
  cost_price: string;
  selling_price: string;
  created_at: string;
  customFields?: { id: string; label: string; value: string }[]
}

const OrderDetail = () => {
  const { user, setNotificationCount } = useAuthStore()
  const { id, customer_id } = useParams();
  const navigate = useNavigate()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["order", id, customer_id],
    queryFn: async () => {
      return await getOrder(id!, customer_id!);
    },
    enabled: !!id && !!customer_id,
  });

  const orderDetailData = data?.data?.data as OrderType

  const header = {
    id: orderDetailData?.id,
    order_id: orderDetailData?.order_id,
    JobTitle: orderDetailData?.job_title,
    status: orderDetailData?.status,
    customer_id: orderDetailData?.customer_id?.toString(),
    designType: orderDetailData?.design_type
  }

  const OrderDetail = {
    orderID: orderDetailData?.order_id,
    orderDate: orderDetailData?.created_at,
    ResinSampleModel: Boolean(orderDetailData?.resin_sample_model),
    Size: orderDetailData?.size,
    TypeofJewelry: orderDetailData?.product_type,
    MetalType: orderDetailData?.metal_type,
    DesignType: orderDetailData?.design_type || '',
    DiamondQuality: orderDetailData?.diamond_quality || '',
    Quantity: orderDetailData?.quantity,
    labels: orderDetailData?.labels,
    businessName: orderDetailData?.business_name,
  }


  const { mutate } = useOrderStatus()
  const { mutate: updateOrder } = useUpdateOrder()
  const { data: orderTagsData } = useOrderTags()
  const adminTags = orderTagsData ?? []
  const queryClient = useQueryClient()

  const handleUpdateOrder = (orderId: string, customerId: string, updates: any) => {
    updateOrder(
      { order_id: orderId, customer_id: customerId, updates },
      { onSuccess: () => queryClient.invalidateQueries({ queryKey: ['order', id, customer_id] }) }
    )
  }

  const fetchTags = useCallback(async (page: number, limit: number, search: string) => {
    const response = await api.get('/admin/order-tags', { params: { page, limit, search } })
    return {
      tags: response.data.data,
      totalPages: response.data.pagination.totalPages
    }
  }, [])

  const isAxiosError = axios.isAxiosError(error)



  const { data: orderLog, isLoading: isOrderLogLoading } = useQuery({
    queryKey: ["order-log", orderDetailData?.id?.toString()],
    queryFn: async () => {
      return await getOrderLog(orderDetailData?.id?.toString());
    },
    enabled: !!orderDetailData?.id,
  });

  const { data: orderCad, isLoading: isOrderCadLoading } = useQuery({
    queryKey: ["order-cad", orderDetailData?.id?.toString()],
    queryFn: async () => {
      return await getOrderCad(orderDetailData?.id?.toString());
    },
    enabled: !!orderDetailData?.id,
  });


  const CADDesigns = orderCad?.data?.data?.map((design: CADDesignType, index: number) => ({
    ...design,
    fileName: `Product Design ${orderDetailData?.order_id}_V${index + 1}`
  }))

  const { data: orderCadSubmitted } = useQuery({
    queryKey: ["order-cad-submitted", orderDetailData?.id?.toString()],
    queryFn: async () => {
      return await getOrderCadSubmiited(orderDetailData?.id?.toString());
    },
    enabled: !!orderDetailData?.id,
  });
  const submittedOrders = orderCadSubmitted?.data?.cad_orders

  const order_activity_log = orderLog?.data?.logs

  const { data: instructions } = useOrderInst(orderDetailData?.id);
  const orderInstruction = instructions?.data?.data;

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Order_${orderDetailData?.order_id || 'Details'}`,
  });

  const joinOrderChat = useSocketStore((s) => s.joinOrderChat);
  const leaveOrderChat = useSocketStore((s) => s.leaveOrderChat);
  useEffect(() => {
    if (!orderDetailData?.order_id) return;
    const orderId = orderDetailData.order_id;
    joinOrderChat(orderId);
    // Leaving on unmount matters more than it used to: the socket store now
    // remembers joined rooms so it can restore them after a reconnect. Without
    // this, navigating away by any route other than handleBack leaves the id in
    // that set forever, and every later reconnect re-joins every order the
    // session has ever opened.
    return () => {
      leaveOrderChat(orderId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetailData?.order_id]);


  const handleBack = () => {
    leaveOrderChat(orderDetailData.order_id);
    navigate(-1)
  }

  // ---- arrow-key navigation between sibling orders -------------------------
  // The list was snapshotted by Dashboard when the order was opened, so it
  // already reflects the kanban column and any tag filter that was active.
  const navItems = useOrderNav((s) => s.items);
  const navIndex = useMemo(
    () => navItems.findIndex((o) => String(o.id) === String(id)),
    [navItems, id]
  );

  const goToSibling = useCallback((delta: number) => {
    if (navIndex < 0) return;
    const target = navItems[navIndex + delta];
    if (!target) return; // ends of the list are hard stops, no wrap-around
    navigate(`/order/${target.id}/${target.orderRef}/${target.customer_id}`, { replace: true });
  }, [navIndex, navItems, navigate]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;

      // never steal the caret from the chat box, a form field or a rich editor
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;

      // ...nor from an open dialog / dropdown, which do their own key handling
      if (document.querySelector('[role="dialog"], [data-radix-popper-content-wrapper]')) return;

      e.preventDefault();
      goToSibling(e.key === 'ArrowRight' ? 1 : -1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goToSibling]);

  const navigateToCadDesign = (id: string) => {
    const product = CADDesigns.find((d: { id: string }) => d.id === id)
    navigate(`/product-view/${id}?from=CAD&order_id=${orderDetailData?.order_id}`, {
      state: { ...product },
    })
  }


  const navigateToCadDesignSubmmited = (order: Record<string, unknown>) => {
    navigate(`/submitted-order/${order.id}?d=${encodeURIComponent(JSON.stringify(order))}`)
  }


  const sendNotification = useSocketStore((s) => s.sendNotification)

  if (isLoading) {
    return <Loader />
  }

  if (isError) {
    return <div className='mt-[16px] mx-[16px]'>
      {
        isError && <ErrorComponent message={isAxiosError ? error?.response?.data?.error : 'Something went wrong!'} />
      }
    </div>
  }

  return (
    <div className='flex flex-col lg:flex-row grow-0 gap-4 p-4 justify-center items-start relative w-full'>
      <div className='hidden'>
        <PrintTemplate
          ref={printRef}
          orderDetailData={orderDetailData}
          OrderDetail={OrderDetail}
          orderInstruction={orderInstruction || []}
        />
      </div>
      <div className='flex flex-col gap-4 w-full lg:w-[65%] order-1 lg:order-none pb-24 lg:pb-0'>
        <AdminHeader orderDetail={header} labels={orderDetailData?.labels} dueDate={orderDetailData?.due_date} adminTags={adminTags} fetchTags={fetchTags} onUpdateOrder={handleUpdateOrder} handleBack={handleBack} updateOrderStatus={mutate} handlePrint={handlePrint} />
        {
          submittedOrders?.length > 0 && <div className='bg-white border border-[#E9EAEB] rounded-md overflow-hidden'>
            <SectionHeader title="Submitted order" sectionIcon={<PackageCheck strokeWidth={1.5} className="w-5 h-5 text-gray-600" />} />
            <div className='text-body2 w-full'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='px-4'>Submitted at</TableHead>
                    <TableHead className='px-4'>Product ID</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className='text-body2'>
                  {
                    submittedOrders?.map((order: Record<string, unknown>) => (
                      <TableRow key={order.id as string} className='my-2 cursor-pointer' onClick={() => {
                        navigateToCadDesignSubmmited(order)
                      }}>
                        <TableCell className='px-4'>{format(new Date(order.created_at as string), 'dd MMM yyyy')}</TableCell>
                        <TableCell className='px-4'>{order.productId as string}</TableCell>
                        <TableCell>
                          <p>View</p>
                        </TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>
            </div>
          </div>
        }
        <div className='flex flex-col gap-4'>
          <OrderDetails order={OrderDetail} isAdmin={true} />
          <SpecialInstructions specialInstructions={orderDetailData?.special_instructions} />
          <Attachments attachmentLinks={orderDetailData?.media_attachment} isAdmin={true} />
          
          <OrderAdditionalInstruction orderDetailData={orderDetailData} />
          <CadDesign CADDesign={CADDesigns} isAdmin={true} isOrderCadLoading={isOrderCadLoading} navigateToCadDesign={navigateToCadDesign} />
          <TimelineLogSection Timeline={order_activity_log || []} isAdmin={false} isLoading={isOrderLogLoading} />
          <Discussion />
          <OrderChecklist orderDetailData={orderDetailData} />
        </div>
      </div>
      <div className='hidden lg:block w-[35%] sticky top-4 self-start h-[calc(100vh-2rem)] overflow-y-auto'>
        <Chat orderDetailData={orderDetailData} />
      </div>
    </div>
  )
}

export default OrderDetail